import { DeleteOutlined, PlusOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Card, Flex, Form, Input, InputNumber, Modal, Select, Space, Table, Typography } from "antd";
import { useDeferredValue, useState } from "react";
import { ApiError, api, unwrap } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useI18n } from "@/i18n";
import type { Product } from "./Products";

// Variants the way sellers know them from the marketplaces: up to two
// variations (Ukuran: S, M, L; Warna: Hitam, Putih); every combination is a
// row with its SKU, price and picture.

export type Group = { name: string; values: string[] };
export type Row = { skuId?: string; price?: number | null; imageId?: string | null };
export type Matrix = { groups: Group[]; rows: Record<string, Row> };
export type SkuLabel = { code: string; name: string };

const SEP = "\u0001";
export const keyOf = (values: string[]) => values.join(SEP);

/** Every combination of the groups' values, in order (one empty row without groups). */
export function combos(groups: Group[]): string[][] {
  const used = groups.filter((g) => g.values.length > 0);
  if (used.length === 0) return [[]];
  return used.reduce<string[][]>((acc, g) => acc.flatMap((c) => g.values.map((v) => [...c, v])), [[]]);
}

export function matrixOf(p: Product): Matrix {
  const groups = p.optionNames.map((name, i) => {
    const values: string[] = [];
    for (const v of p.variants) {
      const x = v.options[i];
      if (x && !values.includes(x)) values.push(x);
    }
    return { name, values };
  });
  const rows: Record<string, Row> = {};
  for (const v of p.variants) rows[keyOf(p.optionNames.length ? v.options : [])] = { skuId: v.skuId, price: v.price, imageId: v.imageId };
  return { groups, rows };
}

/** What is wrong with the variants before saving, if anything. */
export function matrixProblem(m: Matrix): string | null {
  const names = m.groups.map((g) => g.name.trim().toLowerCase());
  if (names.some((n) => !n)) return "prod.var.problem.name";
  if (new Set(names).size !== names.length) return "prod.var.problem.sameName";
  if (m.groups.some((g) => g.values.length === 0)) return "prod.var.problem.values";
  const skus = combos(m.groups).map((c) => m.rows[keyOf(c)]?.skuId);
  if (skus.some((s) => !s)) return "prod.var.problem.sku";
  if (new Set(skus).size !== skus.length) return "prod.var.problem.sameSku";
  return null;
}

/** An SKU of the catalog: searched by code or name; one just made or saved shows its label. */
function SkuPicker({ value, onChange, labels }: { value?: string; onChange: (id: string | undefined) => void; labels: Record<string, SkuLabel> }) {
  const { t } = useI18n();
  const [search, setSearch] = useState("");
  const q = useDeferredValue(search.trim());
  const list = useQuery({
    queryKey: ["skus", "pick", q, true],
    queryFn: () => unwrap(api.GET("/v1/skus", { params: { query: { status: "active", limit: 30, ...(q ? { q } : {}) } } })),
  });
  const options = (list.data?.items ?? []).map((s) => ({ value: s.id, code: s.code, name: s.name }));
  if (value && !options.some((o) => o.value === value) && labels[value]) options.unshift({ value, ...labels[value] });
  return (
    <Select<string>
      allowClear
      showSearch={{ filterOption: false, onSearch: setSearch }}
      value={value}
      onChange={onChange}
      loading={list.isFetching}
      placeholder={t("prod.var.pickSku")}
      style={{ width: "100%" }}
      options={options.map((o) => ({
        value: o.value,
        label: (
          <span>
            <span className="z-num">{o.code}</span> · {o.name}
          </span>
        ),
      }))}
    />
  );
}

const slug = (s: string) =>
  s
    .normalize("NFKD")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/[\s_]+/g, "-")
    .toUpperCase();

export function VariantMatrix({
  value,
  onChange,
  labels,
  onLabel,
  productName,
  gallery,
  disabled,
}: {
  value: Matrix;
  onChange: (m: Matrix) => void;
  labels: Record<string, SkuLabel>;
  onLabel: (id: string, l: SkuLabel) => void;
  productName: string;
  gallery: { id: string }[];
  disabled: boolean;
}) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const [bulkPrice, setBulkPrice] = useState<number | null>(null);
  const [creatingFor, setCreatingFor] = useState<string | null>(null);
  const [autoOpen, setAutoOpen] = useState(false);
  const [prefix, setPrefix] = useState("");
  const [autoBusy, setAutoBusy] = useState(false);
  const { groups, rows } = value;
  const all = combos(groups);
  const setGroups = (g: Group[]) => onChange({ groups: g, rows });
  const setRow = (key: string, patch: Partial<Row>) => onChange({ groups, rows: { ...rows, [key]: { ...rows[key], ...patch } } });

  const missingSku = all.filter((c) => !rows[keyOf(c)]?.skuId);
  const autoSku = async () => {
    const base = slug(prefix);
    if (!base) return;
    setAutoBusy(true);
    const next = { ...rows };
    let made = 0;
    try {
      for (const c of missingSku) {
        const code = [base, ...c.map(slug)].filter(Boolean).join("-").slice(0, 64);
        const name = [productName.trim() || base, ...c].join(" ").slice(0, 255);
        let id: string | undefined;
        try {
          const s = await unwrap(api.POST("/v1/skus", { body: { code, name } }));
          id = s.id;
          onLabel(s.id, { code: s.code, name: s.name });
          made++;
        } catch (err) {
          // The code exists already: use that SKU.
          if (!(err instanceof ApiError && err.status === 409)) throw err;
          const found = await unwrap(api.GET("/v1/skus", { params: { query: { q: code, limit: 5 } } }));
          const hit = found.items.find((s) => s.code.toUpperCase() === code);
          if (hit) {
            id = hit.id;
            onLabel(hit.id, { code: hit.code, name: hit.name });
          }
        }
        if (id) next[keyOf(c)] = { ...next[keyOf(c)], skuId: id };
      }
      onChange({ groups, rows: next });
      void queryClient.invalidateQueries({ queryKey: ["skus"] });
      void message.success(t("prod.var.autoDone", { n: made }));
      setAutoOpen(false);
    } catch (err) {
      onChange({ groups, rows: next });
      void message.error(errorText(err));
    } finally {
      setAutoBusy(false);
    }
  };

  return (
    <Card size="small" title={t("prod.variantsTitle")}>
      <Space orientation="vertical" size={12} style={{ width: "100%" }}>
        {groups.map((g, i) => (
          <Flex key={i} gap={12} align="start" wrap>
            <Space orientation="vertical" size={2} style={{ width: 200 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t("prod.var.groupN", { n: i + 1 })}
              </Typography.Text>
              <Input
                value={g.name}
                maxLength={30}
                disabled={disabled}
                placeholder={t(i === 0 ? "prod.var.namePlaceholder1" : "prod.var.namePlaceholder2")}
                onChange={(e) => setGroups(groups.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
              />
            </Space>
            <Space orientation="vertical" size={2} style={{ flex: 1, minWidth: 240 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t("prod.var.values")}
              </Typography.Text>
              <Select
                mode="tags"
                open={false}
                suffix={null}
                disabled={disabled}
                value={g.values}
                tokenSeparators={[","]}
                placeholder={t(i === 0 ? "prod.var.valuesPlaceholder1" : "prod.var.valuesPlaceholder2")}
                onChange={(vals: string[]) =>
                  setGroups(groups.map((x, j) => (j === i ? { ...x, values: [...new Set(vals.map((v) => v.trim()).filter(Boolean))].slice(0, 50) } : x)))
                }
              />
            </Space>
            {!disabled && (
              <Button type="text" danger icon={<DeleteOutlined />} style={{ marginTop: 22 }} onClick={() => setGroups(groups.filter((_, j) => j !== i))} />
            )}
          </Flex>
        ))}
        {!disabled && groups.length < 2 && (
          <Button icon={<PlusOutlined />} onClick={() => setGroups([...groups, { name: "", values: [] }])}>
            {t("prod.var.addGroup")}
          </Button>
        )}
        {groups.length === 0 && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t("prod.var.noGroups")}
          </Typography.Text>
        )}

        {!disabled && all.length > 1 && (
          <Flex gap={8} wrap align="center">
            <InputNumber min={1} precision={0} prefix="Rp" placeholder={t("prod.var.bulkPrice")} value={bulkPrice} onChange={setBulkPrice} style={{ width: 200 }} />
            <Button
              disabled={!bulkPrice}
              onClick={() => onChange({ groups, rows: Object.fromEntries(all.map((c) => [keyOf(c), { ...rows[keyOf(c)], price: bulkPrice }])) })}
            >
              {t("prod.var.applyAll")}
            </Button>
            <div style={{ flex: 1 }} />
            <Button icon={<ThunderboltOutlined />} disabled={missingSku.length === 0} onClick={() => (setPrefix(slug(productName).split("-")[0] ?? ""), setAutoOpen(true))}>
              {t("prod.var.autoSku", { n: missingSku.length })}
            </Button>
          </Flex>
        )}

        <Table
          size="small"
          pagination={false}
          rowKey={(c) => keyOf(c)}
          dataSource={all}
          scroll={{ x: true }}
          columns={[
            ...groups
              .filter((g) => g.values.length > 0)
              .map((g, i) => ({
                title: g.name || t("prod.var.groupN", { n: i + 1 }),
                width: 110,
                render: (_: unknown, c: string[]) => <Typography.Text strong>{c[i]}</Typography.Text>,
              })),
            {
              title: t("prod.sku"),
              render: (_: unknown, c: string[]) => (
                <Space.Compact style={{ width: "100%", minWidth: 260 }}>
                  <SkuPicker labels={labels} value={rows[keyOf(c)]?.skuId} onChange={(id) => setRow(keyOf(c), { skuId: id })} />
                  {!disabled && <Button icon={<PlusOutlined />} title={t("prod.newSku")} onClick={() => setCreatingFor(keyOf(c))} />}
                </Space.Compact>
              ),
            },
            {
              title: t("prod.variantPrice"),
              width: 160,
              render: (_: unknown, c: string[]) => (
                <InputNumber
                  min={1}
                  precision={0}
                  prefix="Rp"
                  disabled={disabled}
                  placeholder={t("prod.samePrice")}
                  value={rows[keyOf(c)]?.price ?? null}
                  onChange={(v) => setRow(keyOf(c), { price: v })}
                  style={{ width: 150 }}
                />
              ),
            },
            ...(gallery.length > 0
              ? [
                  {
                    title: t("prod.variantImage"),
                    width: 120,
                    render: (_: unknown, c: string[]) => (
                      <Select
                        allowClear
                        disabled={disabled}
                        value={rows[keyOf(c)]?.imageId ?? undefined}
                        onChange={(v?: string) => setRow(keyOf(c), { imageId: v ?? null })}
                        style={{ width: 110 }}
                        options={gallery.map((im, j) => ({ value: im.id, label: t("prod.imageN", { n: j + 1 }) }))}
                      />
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Space>

      <NewSkuModal
        open={creatingFor !== null}
        suggestion={
          creatingFor !== null
            ? {
                code: [slug(productName).split("-")[0] ?? "", ...creatingFor.split(SEP).map(slug)].filter(Boolean).join("-").slice(0, 64),
                name: [productName, ...creatingFor.split(SEP)].filter(Boolean).join(" ").slice(0, 255),
              }
            : undefined
        }
        onClose={() => setCreatingFor(null)}
        onCreated={(s) => {
          onLabel(s.id, { code: s.code, name: s.name });
          if (creatingFor !== null) setRow(creatingFor, { skuId: s.id });
        }}
      />
      <Modal
        open={autoOpen}
        title={t("prod.var.autoTitle")}
        onCancel={() => setAutoOpen(false)}
        onOk={() => void autoSku()}
        okText={t("prod.var.autoOk", { n: missingSku.length })}
        okButtonProps={{ disabled: !slug(prefix) }}
        cancelText={t("common.cancel")}
        confirmLoading={autoBusy}
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary">{t("prod.var.autoHelp")}</Typography.Paragraph>
        <Form layout="vertical">
          <Form.Item label={t("prod.var.prefix")}>
            <Input value={prefix} maxLength={20} onChange={(e) => setPrefix(e.target.value)} className="z-num" />
          </Form.Item>
        </Form>
        {missingSku.slice(0, 4).map((c) => (
          <div key={keyOf(c)}>
            <Typography.Text className="z-num">{[slug(prefix), ...c.map(slug)].filter(Boolean).join("-")}</Typography.Text>
          </div>
        ))}
        {missingSku.length > 4 && <Typography.Text type="secondary">…</Typography.Text>}
      </Modal>
    </Card>
  );
}

/** "SKU baru" without leaving the product: code and name, like the catalog. */
function NewSkuModal({
  open,
  suggestion,
  onClose,
  onCreated,
}: {
  open: boolean;
  suggestion?: { code: string; name: string };
  onClose: () => void;
  onCreated: (s: { id: string; code: string; name: string }) => void;
}) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<{ code: string; name: string }>();
  const [busy, setBusy] = useState(false);
  const submit = async (v: { code: string; name: string }) => {
    setBusy(true);
    try {
      const s = await unwrap(api.POST("/v1/skus", { body: { code: v.code.trim(), name: v.name.trim() } }));
      void queryClient.invalidateQueries({ queryKey: ["skus"] });
      onCreated(s);
      onClose();
    } catch (err) {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open={open}
      title={t("prod.newSku")}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t("common.add")}
      cancelText={t("common.cancel")}
      confirmLoading={busy}
      destroyOnHidden
      afterOpenChange={(o) => o && suggestion && form.setFieldsValue(suggestion)}
      afterClose={() => form.resetFields()}
    >
      <Form form={form} layout="vertical" requiredMark={false} onFinish={(v) => void submit(v)}>
        <Form.Item name="code" label={t("prod.skuCode")} extra={t("prod.skuCodeHelp")} rules={[{ required: true, whitespace: true, max: 64 }]}>
          <Input autoFocus className="z-num" />
        </Form.Item>
        <Form.Item name="name" label={t("prod.skuName")} rules={[{ required: true, whitespace: true, max: 255 }]}>
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
}
